import React, { useState, useEffect } from "react";

/* ─────────────────────────────────────────
   DEFAULT METHODOLOGY (Bacterial Whole Genome Sequencing)
───────────────────────────────────────── */
const DEFAULT_METHODOLOGY = `Bacterial Whole Genome Sequencing (WGS) Workflow:

Genomic DNA Extraction & Quality Check:
High-molecular-weight genomic DNA was extracted from pure bacterial cultures using standard DNA purification kits. DNA quality and concentration were verified using Qubit Fluorometric Quantitation and agarose gel electrophoresis. Sequencing libraries were constructed following standard short/long-read library preparation protocols, including enzymatic fragmentation, end-repair, A-tailing, and adapter ligation.

Sequencing & Quality Control:
Sequencing was performed on high-throughput sequencing platforms. Raw sequencing reads were quality-assessed using FASTQC (0.12.1) to determine per-base quality scores, GC content distribution, and read length metrics.

Genome Assembly & Quality Assessment:
De novo assembly of bacterial genomes was carried out using Unicycler (v0.5.1). Assembly completeness, contig continuity, and metrics were evaluated via QUAST (5.3.0) and BUSCO (v6.0).

Genome Annotation & Speciality Genes Screening:
Gene prediction and functional annotation were conducted using Bakta (1.12.0). Antimicrobial Resistance (AMR) genes, virulence factors, transporters, and therapeutic targets were identified by searching against curated databases including CARD-RGI (6.0.5), VFDB (2022 Update), TCDB (2021 Update), and TTD (2024 Version).

Phylogenetic Analysis:
Phylogenetic relationships and pan-genome analysis were inferred using Roary (3.13.0) for high-resolution lineage typing.`;

const DEFAULT_SOFTWARE = [
  { item: "Quality Control", software: "FASTQC", version: "0.12.1" },
  { item: "Assembly", software: "Unicycler", version: "v0.5.1" },
  { item: "Assembly Quality", software: "QUAST", version: "5.3.0" },
  { item: "Assembly Completeness", software: "BUSCO", version: "v6.0" },
  { item: "Genome Annotation", software: "Bakta", version: "1.12.0" },
  { item: "Speciality Genes (Virulence)", software: "VFDB", version: "2022 Update" },
  { item: "Speciality Genes (AMR)", software: "CARD-RGI", version: "6.0.5" },
  { item: "Speciality Genes (Transporters)", software: "TCDB", version: "2021 Update" },
  { item: "Speciality Genes (Therapeutic Targets)", software: "TTD", version: "2024 Version" },
  { item: "Phylogenetic Analysis", software: "Roary", version: "3.13.0" },
];

/* ─────────────────────────────────────────
   DEFAULT METHODOLOGY (BV-BRC Module)
───────────────────────────────────────── */
const BVBRC_DEFAULT_METHODOLOGY = `Bacterial Whole Genome Sequencing (WGS) Workflow:

Genomic DNA Extraction & Quality Check:
High-molecular-weight genomic DNA was extracted from pure bacterial cultures using standard DNA purification kits. DNA quality and concentration were verified using Qubit Fluorometric Quantitation and agarose gel electrophoresis. Sequencing libraries were constructed following standard sequencing library preparation protocols.

Sequencing & Quality Control:
Sequencing was performed on high-throughput sequencing platforms. Raw sequencing reads were quality-assessed using FASTQC (0.12.1) to determine per-base quality scores, GC content distribution, and read length metrics.

Genome Assembly & Quality Assessment:
De novo assembly of bacterial genomes was carried out using Unicycler (v0.4.8). Assembly completeness, contig continuity, and metrics were evaluated via QUAST (v5.2.0).

Genome Annotation:
Gene prediction and functional annotation were conducted using RASTtk (1.3) via the BV-BRC (Bacterial and Viral Bioinformatics Resource Center) pipeline. Comprehensive genomic features, coding sequences, and functional subsystems were characterized.

Average Nucleotide Identity (ANI) & Phylogenetic Analysis:
Average Nucleotide Identity (ANI) was determined using fastANI (1.34). Phylogenetic relationships and comparative tree inference were conducted using PATRIC (v3.2.76).`;

const BVBRC_DEFAULT_SOFTWARE = [
  { item: "Quality control", software: "FASTQC", version: "0.12.1" },
  { item: "Assembly", software: "Unicycler", version: "v0.4.8" },
  { item: "Assembly Quality", software: "QUAST", version: "v5.2.0" },
  { item: "Genome Annotation", software: "RASTtk", version: "1.3" },
  { item: "ANI", software: "fastANI", version: "1.34" },
  { item: "Phylogenetic Analysis", software: "PATRIC", version: "v3.2.76" },
];

/* ─────────────────────────────────────────
   STYLES (Bacterial WGS Mild Teal Design System)
───────────────────────────────────────── */
function getStyle(dark) {
  const bg = dark ? "#060f1e" : "#f4f6f8";
  const cardBg = dark ? "#0b1728" : "#ffffff";
  const cardBorder = dark ? "#162842" : "#e5e7eb";
  const text = dark ? "#f1f5f9" : "#111827";
  const textSub = dark ? "#94a3b8" : "#6b7280";
  const textHead = dark ? "#e2e8f0" : "#374151";
  const inputBg = dark ? "#081322" : "#ffffff";
  const inputBorder = dark ? "#1e3a5f" : "#e5e7eb";

  // Mild Teal palette (calm, scientific, eye-friendly, not vibrant yellow)
  const tealAccent = dark ? "#14b8a6" : "#0d9488";
  const tealAccentDk = dark ? "#0d9488" : "#0f766e";
  const tealBg = dark ? "rgba(20, 184, 166, 0.12)" : "#f0fdfa";
  const tealBorder = dark ? "rgba(20, 184, 166, 0.28)" : "#ccfbf1";
  const tealDashed = dark ? "#115e59" : "#99f6e4";
  const tealFocusRing = dark ? "rgba(20, 184, 166, 0.25)" : "rgba(13, 148, 136, 0.16)";

  return `
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');
.bac-page *, .bac-page *::before, .bac-page *::after { box-sizing: border-box; }
.bac-page { font-family:'Barlow',sans-serif; background:${bg}; min-height:100vh; padding:0 0 80px; }
.bac-page .bac-breadcrumb { display:flex; align-items:center; gap:6px; padding:14px 32px; font-size:13px; color:${textSub}; border-bottom:1px solid ${cardBorder}; background:${cardBg}; }
.bac-page .bac-breadcrumb .bc-active { color:${tealAccent}; font-weight:600; }
.bac-page .bac-breadcrumb .bc-sep { color:${dark ? '#334155' : '#d1d5db'}; }
.bac-page .bac-content { max-width:960px; margin:0 auto; padding:28px 16px; display:flex; flex-direction:column; gap:20px; }
.bac-page .module-switcher { display:flex; gap:12px; background:${cardBg}; border:1.5px solid ${cardBorder}; border-radius:12px; padding:8px; box-shadow:0 1px 4px rgba(0,0,0,${dark ? '0.25' : '0.04'}); }
.bac-page .module-btn { flex:1; display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 20px; border-radius:9px; border:1.5px solid transparent; background:transparent; cursor:pointer; font-family:'Barlow',sans-serif; transition:all .18s ease; text-align:left; }
.bac-page .module-btn:hover { background:${tealBg}; border-color:${tealBorder}; }
.bac-page .module-btn.active { background:${tealBg}; border-color:${tealAccent}; box-shadow:0 2px 8px rgba(13,148,136,${dark ? '0.25' : '0.12'}); }
.bac-page .module-btn-content { display:flex; align-items:center; gap:12px; }
.bac-page .module-btn-title { font-size:15px; font-weight:800; color:${text}; line-height:1.2; }
.bac-page .module-btn.active .module-btn-title { color:${tealAccentDk}; }
.bac-page .module-btn-sub { font-size:12px; color:${textSub}; margin-top:3px; }
.bac-page .module-badge { display:inline-flex; align-items:center; gap:4px; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.6px; background:${tealAccent}; color:#ffffff; }
.bac-page .section-card { background:${cardBg}; border-radius:12px; border:1px solid ${cardBorder}; padding:28px 32px; box-shadow:0 1px 4px rgba(0,0,0,${dark ? '0.3' : '0.06'}); }
.bac-page .section-header { display:flex; align-items:flex-start; gap:14px; margin-bottom:24px; }
.bac-page .section-header-text h3 { margin:0 0 3px; font-size:19px; font-weight:800; color:${text}; }
.bac-page .section-header-text p { margin:0; font-size:13px; color:${textSub}; }
.bac-page .divider { height:1px; background:${dark ? '#162842' : '#f3f4f6'}; margin:20px 0; }
.bac-page .sub-label { font-size:13px; font-weight:800; text-transform:uppercase; letter-spacing:.8px; color:${tealAccentDk}; margin-bottom:12px; padding-bottom:6px; border-bottom:1.5px solid ${tealBorder}; }
.bac-page .g2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.bac-page .g3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; }
@media(max-width:768px){ .bac-page .g2, .bac-page .g3 { grid-template-columns:1fr; } }
.bac-page .field { display:flex; flex-direction:column; gap:5px; }
.bac-page .field > label { font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; color:${textHead}; }
.bac-page .field > label .req { color:#dc2626; margin-left:2px; }
.bac-page .field input { padding:10px 14px; border:1.5px solid ${inputBorder}; border-radius:8px; font-size:14px; font-family:'Barlow',sans-serif; color:${text}; background:${inputBg}; outline:none; width:100%; transition:border-color .18s, box-shadow .18s; }
.bac-page .field input:focus { border-color:${tealAccent}; box-shadow:0 0 0 3px ${tealFocusRing}; }
.bac-page .field input::placeholder { color:${dark ? '#475569' : '#c9cdd4'}; }

.bac-page .drop-area { border:2px dashed ${tealDashed}; border-radius:10px; padding:20px 16px; text-align:center; cursor:pointer; background:${tealBg}; position:relative; transition:all .18s; }
.bac-page .drop-area:hover, .bac-page .drop-area.drag { border-color:${tealAccent}; background:${dark ? 'rgba(20,184,166,0.2)' : '#e6fffa'}; }
.bac-page .drop-area input[type=file] { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
.bac-page .drop-main { font-size:13px; font-weight:600; color:${tealAccentDk}; margin-top:6px; }
.bac-page .drop-hint { font-size:11px; color:${textSub}; margin-top:2px; }

.bac-page .chip-list { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
.bac-page .chip { display:inline-flex; align-items:center; gap:5px; background:${tealBg}; color:${tealAccentDk}; border:1px solid ${tealBorder}; border-radius:20px; padding:3px 10px 3px 8px; font-size:11px; font-weight:500; max-width:260px; }
.bac-page .chip span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.bac-page .chip button { background:none; border:none; cursor:pointer; color:${tealAccentDk}; font-size:13px; line-height:1; padding:0; margin-left:2px; }

.bac-page .info-banner { display:flex; align-items:flex-start; gap:10px; background:${tealBg}; border:1px solid ${tealBorder}; border-radius:8px; padding:12px 14px; margin-bottom:16px; }
.bac-page .info-banner-icon { font-size:15px; flex-shrink:0; margin-top:1px; }
.bac-page .info-banner-text { font-size:12px; color:${tealAccentDk}; line-height:1.6; }
.bac-page .info-banner-text strong { font-weight:700; }

.bac-page .software-table { width:100%; border-collapse:collapse; margin-top:10px; }
.bac-page .software-table th { text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; color:${textHead}; padding:8px; border-bottom:1.5px solid ${cardBorder}; }
.bac-page .software-table td { padding:6px 8px; vertical-align:middle; }
.bac-page .software-table input { padding:8px 10px; border:1.5px solid ${inputBorder}; border-radius:6px; font-size:13px; outline:none; width:100%; font-family:'Barlow',sans-serif; color:${text}; background:${inputBg}; }
.bac-page .software-table input:focus { border-color:${tealAccent}; box-shadow:0 0 0 2px ${tealFocusRing}; }
.bac-page .btn-add-step { background:${tealBg}; color:${tealAccentDk}; border:1px solid ${tealBorder}; border-radius:6px; padding:6px 14px; font-size:12px; font-weight:600; cursor:pointer; margin-top:10px; transition:all .15s; }
.bac-page .btn-add-step:hover { background:${dark ? 'rgba(20,184,166,0.2)' : '#ccfbf1'}; }
.bac-page .btn-remove-step { background:none; border:none; color:#dc2626; cursor:pointer; font-size:14px; padding:4px; border-radius:4px; }
.bac-page .btn-remove-step:hover { background:${dark ? '#3c1818' : '#fee2e2'}; }

.bac-page .file-tree { background:${dark ? '#081322' : '#f8fafc'}; border-radius:10px; border:1.5px solid ${cardBorder}; padding:14px 16px; margin-top:12px; font-family:'Courier New',monospace; }
.bac-page .ft-title { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.7px; color:${textSub}; margin-bottom:10px; }
.bac-page .ft-root { font-size:12px; color:${text}; margin-bottom:4px; display:flex; align-items:center; gap:5px; }
.bac-page .ft-line { display:flex; align-items:flex-start; margin:2px 0; }
.bac-page .ft-branch { font-size:12px; color:${tealAccent}; white-space:pre; user-select:none; flex-shrink:0; margin-right:2px; }
.bac-page .ft-item { font-size:12px; color:${textHead}; line-height:1.6; }
.bac-page .ft-item.folder { color:${tealAccentDk}; font-weight:600; }
.bac-page .ft-item.note { color:${textSub}; font-style:italic; }

.bac-page .method-area { width:100%; min-height:240px; resize:vertical; border:1.5px solid ${inputBorder}; border-radius:10px; padding:14px 16px; font-size:14px; line-height:1.85; color:${text}; font-family:'Barlow',sans-serif; background:${inputBg}; outline:none; box-sizing:border-box; }
.bac-page .method-area:focus { border-color:${tealAccent}; box-shadow:0 0 0 3px ${tealFocusRing}; }
.bac-page .method-meta { display:flex; align-items:center; justify-content:space-between; margin-top:7px; padding:0 2px; }
.bac-page .method-word-count { font-size:11px; color:${textSub}; font-variant-numeric:tabular-nums; }
.bac-page .method-clear { background:none; border:none; cursor:pointer; font-size:11px; color:#dc2626; font-weight:700; padding:2px 6px; border-radius:5px; }
.bac-page .method-clear:hover { background:${dark ? '#3c1818' : '#fee2e2'}; }

.submit-bar { position:fixed; bottom:0; left:0; right:0; background:${cardBg}; border-top:1px solid ${cardBorder}; padding:14px 32px; display:flex; align-items:center; justify-content:flex-end; gap:12px; box-shadow:0 -2px 12px rgba(0,0,0,${dark ? '0.3' : '0.07'}); z-index:100; }
.btn-submit { background:${tealAccent}; color:#fff; border:none; border-radius:8px; padding:12px 36px; font-size:14px; font-weight:600; font-family:'Barlow',sans-serif; cursor:pointer; display:flex; align-items:center; gap:8px; transition:background .18s, transform .15s; }
.btn-submit:hover:not(:disabled) { background:${tealAccentDk}; transform:translateY(-1px); }
.btn-submit:disabled { opacity:0.7; cursor:not-allowed; }
.submit-error { font-size:12px; font-weight:600; color:#dc2626; background:${dark ? '#3c1818' : '#fee2e2'}; border:1px solid ${dark ? '#7f1d1d' : '#fca5a5'}; border-radius:7px; padding:7px 12px; }
.submit-success { font-size:12px; font-weight:600; color:#15803d; background:${dark ? 'rgba(34,197,94,0.15)' : '#f0fdf4'}; border:1px solid ${dark ? '#166534' : '#86efac'}; border-radius:7px; padding:7px 12px; }

@keyframes bac-spin { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }
`;
}

function injectStyle(dark) {
  let s = document.getElementById("bac-wgs-style");
  if (!s) {
    s = document.createElement("style");
    s.id = "bac-wgs-style";
    document.head.appendChild(s);
  }
  s.textContent = getStyle(dark);
}

/* ─────────────────────────────────────────
   FILE TREE PREVIEW COMPONENT
───────────────────────────────────────── */
function FileTree({ title, lines }) {
  return (
    <div className="file-tree">
      <div className="ft-title">{title}</div>
      {lines.map((l, i) => {
        if (l.type === "root") return (
          <div key={i} className="ft-root"><span>📁</span><span style={{ color: "#2563eb", fontWeight: 700 }}>{l.text}</span></div>
        );
        return (
          <div key={i} className="ft-line">
            <span className="ft-branch">{l.branch}</span>
            <span className={`ft-item ${l.cls || ""}`}>{l.text}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────
   DROP AREA COMPONENT
───────────────────────────────────────── */
function DropArea({ accept, mainLabel, subLabel, icon = "📁", file, onFile, isMulti = false, files = [], onFiles }) {
  const [drag, setDrag] = useState(false);

  const handleDrop = e => {
    e.preventDefault();
    setDrag(false);
    if (isMulti) {
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length && onFiles) onFiles([...files, ...dropped]);
    } else {
      const f = e.dataTransfer.files[0];
      if (f && onFile) onFile(f);
    }
  };

  const handleChange = e => {
    if (isMulti) {
      const selected = Array.from(e.target.files);
      if (selected.length && onFiles) onFiles([...files, ...selected]);
    } else {
      const f = e.target.files[0];
      if (f && onFile) onFile(f);
    }
  };

  return (
    <div
      className={`drop-area ${drag ? "drag" : ""}`}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
    >
      <input type="file" accept={accept} multiple={isMulti} onChange={handleChange} />
      <div style={{ fontSize: 24, lineHeight: 1 }}>{file || (isMulti && files.length > 0) ? "✅" : icon}</div>
      {isMulti ? (
        files.length > 0 ? (
          <div className="drop-main">{files.length} file(s) selected</div>
        ) : (
          <>
            <div className="drop-main">{mainLabel}</div>
            <div className="drop-hint">{subLabel}</div>
          </>
        )
      ) : file ? (
        <>
          <div className="drop-main">{file.name}</div>
          <div className="drop-hint">{(file.size / 1024).toFixed(1)} KB</div>
        </>
      ) : (
        <>
          <div className="drop-main">{mainLabel}</div>
          <div className="drop-hint">{subLabel}</div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function BacterialWgsReportUpload({ setReportUrls, setActiveTab, dark }) {
  useEffect(() => {
    injectStyle(dark);
    return () => {
      const s = document.getElementById("bac-wgs-style");
      if (s) s.remove();
    };
  }, [dark]);

  // Form states
  const [moduleType, setModuleType] = useState("bvbrc"); // 'bvbrc' | 'standard'
  const [ci, setCi] = useState({
    reportTitle: "Bacterial WGS",
    clientName: "",
    projectId: "",
    institution: "",
    contact: "",
    salesperson: ""
  });

  const [pi, setPi] = useState({
    speciesName: "Escherichia coli",
    taxonomy: "Bacteria; Pseudomonadota; Gammaproteobacteria; Enterobacterales; Enterobacteriaceae; Escherichia; Escherichia coli"
  });

  const [rawSequence, setRawSequence] = useState(null);
  const [rawMultiqc, setRawMultiqc] = useState(null);
  const [rawQuality, setRawQuality] = useState(null);
  const [rawQcSummary, setRawQcSummary] = useState(null);

  const [cleanSequence, setCleanSequence] = useState(null);
  const [cleanMultiqc, setCleanMultiqc] = useState(null);
  const [cleanQuality, setCleanQuality] = useState(null);
  const [cleanQcSummary, setCleanQcSummary] = useState(null);

  const [qcPlot1, setQcPlot1] = useState(null);
  const [qcPlot2, setQcPlot2] = useState(null);
  const [qcPlot3, setQcPlot3] = useState(null);
  const [qcPlot4, setQcPlot4] = useState(null);
  const [qcPlot5, setQcPlot5] = useState(null);
  const [qcPlot6, setQcPlot6] = useState(null);

  const [bandagePlot, setBandagePlot] = useState(null);
  const [quastZip, setQuastZip] = useState(null);
  const [contigsFasta, setContigsFasta] = useState(null);
  const [quastPdfs, setQuastPdfs] = useState([]);
  const [resourcesZip, setResourcesZip] = useState(null);
  const [annotationTsv, setAnnotationTsv] = useState(null);
  const [annotatedGenomeFeatures, setAnnotatedGenomeFeatures] = useState(null);
  const [proteinFeatures, setProteinFeatures] = useState(null);
  const [circosPlot, setCircosPlot] = useState(null);
  const [fullGenomeReportHtml, setFullGenomeReportHtml] = useState(null);
  const [showAdvancedAnn, setShowAdvancedAnn] = useState(false);

  const [cardTxt, setCardTxt] = useState(null);
  const [tcsbTxt, setTcsbTxt] = useState(null);
  const [vfdbTxt, setVfdbTxt] = useState(null);
  const [ttdTxt, setTtdTxt] = useState(null);

  const [phylogenyImage, setPhylogenyImage] = useState(null);
  const [phylogenyTxt, setPhylogenyTxt] = useState(null);

  const [softwareRows, setSoftwareRows] = useState(BVBRC_DEFAULT_SOFTWARE.map(r => ({ ...r })));
  const [methodologyText, setMethodologyText] = useState(BVBRC_DEFAULT_METHODOLOGY);
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState("html");
  const [generatedReport, setGeneratedReport] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSwitchModule = (type) => {
    if (type === moduleType) return;
    setModuleType(type);
    if (type === "bvbrc") {
      setCi(p => ({
        ...p,
        reportTitle: (!p.reportTitle || p.reportTitle === "Bacterial WGS — BV-BRC") ? "Bacterial WGS" : p.reportTitle 
      }));
      setSoftwareRows(BVBRC_DEFAULT_SOFTWARE.map(r => ({ ...r })));
      setMethodologyText(BVBRC_DEFAULT_METHODOLOGY);
    } else {
      setCi(p => ({
        ...p,
        reportTitle: p.reportTitle === "Bacterial WGS — BV-BRC" ? "Bacterial WGS" : p.reportTitle
      }));
      setSoftwareRows(DEFAULT_SOFTWARE.map(r => ({ ...r })));
      setMethodologyText(DEFAULT_METHODOLOGY);
    }
  };

  const handleCi = e => setCi(p => ({ ...p, [e.target.name]: e.target.value }));
  const handlePi = e => setPi(p => ({ ...p, [e.target.name]: e.target.value }));

  const addSoftwareRow = () => setSoftwareRows(p => [...p, { item: "", software: "", version: "" }]);
  const removeSoftwareRow = (i) => setSoftwareRows(p => p.filter((_, idx) => idx !== i));
  const editSoftwareRow = (i, key, val) => setSoftwareRows(p => p.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

  const wordCount = methodologyText.trim() ? methodologyText.trim().split(/\s+/).filter(Boolean).length : 0;

  const triggerHtmlDownload = async (reportUrl, filename) => {
    try {
      const fileRes = await fetch(reportUrl);
      if (fileRes.ok) {
        const htmlBlob = await fileRes.blob();
        const blobUrl = window.URL.createObjectURL(htmlBlob);
        const dlLink = document.createElement("a");
        dlLink.href = blobUrl;
        dlLink.download = filename;
        document.body.appendChild(dlLink);
        dlLink.click();
        setTimeout(() => {
          document.body.removeChild(dlLink);
          window.URL.revokeObjectURL(blobUrl);
        }, 1000);
        return;
      }
    } catch (e) {
      console.warn("Direct blob download error, falling back:", e);
    }
    const dlLink = document.createElement("a");
    dlLink.href = `/api/bacterial-wgs/download-report?file=${encodeURIComponent(filename)}`;
    dlLink.download = filename;
    document.body.appendChild(dlLink);
    dlLink.click();
    setTimeout(() => document.body.removeChild(dlLink), 500);
  };

  const handleUpload = async (format = 'html') => {
    setActionType(format);
    setError("");

    // Fast-path: if report was already generated and user clicks "Export PDF"
    if (format === "pdf" && generatedReport?.pdfFilename) {
  setLoading(true);
  try {
    await triggerHtmlDownload(
      generatedReport.fullPdfUrl || generatedReport.pdfUrl,
      generatedReport.pdfFilename
    );
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
  return;
}

    const fd = new FormData();
    fd.append("moduleType", moduleType);
    fd.append("clientInfo", JSON.stringify(ci));
    fd.append("projectInfo", JSON.stringify(pi));
    fd.append("methodology", methodologyText);
    fd.append("software", JSON.stringify(softwareRows));

    if (rawSequence) fd.append("raw_sequence", rawSequence);
    if (rawMultiqc) fd.append("raw_multiqc", rawMultiqc);
    if (rawQuality) fd.append("raw_quality", rawQuality);
    if (rawQcSummary) fd.append("rawQcSummary", rawQcSummary);

    if (cleanSequence) fd.append("clean_sequence", cleanSequence);
    if (cleanMultiqc) fd.append("clean_multiqc", cleanMultiqc);
    if (cleanQuality) fd.append("clean_quality", cleanQuality);
    if (cleanQcSummary) fd.append("cleanQcSummary", cleanQcSummary);

    if (qcPlot1) fd.append("qcImages", qcPlot1);
    if (qcPlot2) fd.append("qcImages", qcPlot2);
    if (qcPlot3) fd.append("qcImages", qcPlot3);
    if (qcPlot4) fd.append("qcImages", qcPlot4);
    if (qcPlot5) fd.append("qcImages", qcPlot5);
    if (qcPlot6) fd.append("qcImages", qcPlot6);

    if (bandagePlot) fd.append("bandagePlot", bandagePlot);
    if (quastZip) fd.append("quastZip", quastZip);
    if (contigsFasta) fd.append("contigsFasta", contigsFasta);
    quastPdfs.forEach(f => fd.append("quastPdfs", f));
    if (resourcesZip) fd.append("resourcesZip", resourcesZip);
    if (annotationTsv) fd.append("annotationTsv", annotationTsv);
    if (annotatedGenomeFeatures) fd.append("annotatedGenomeFeatures", annotatedGenomeFeatures);
    if (proteinFeatures) fd.append("proteinFeatures", proteinFeatures);
    if (circosPlot) fd.append("circosPlot", circosPlot);
    if (fullGenomeReportHtml) {
      fd.append("fullGenomeReportHtml", fullGenomeReportHtml);
      fd.append("full_genome_report_html", fullGenomeReportHtml);
    }

    if (cardTxt) fd.append("cardTxt", cardTxt);
    if (tcsbTxt) fd.append("tcsbTxt", tcsbTxt);
    if (vfdbTxt) fd.append("vfdbTxt", vfdbTxt);
    if (ttdTxt)  fd.append("ttdTxt", ttdTxt);

    if (phylogenyImage) fd.append("phylogenyImage", phylogenyImage);
    if (phylogenyTxt)   fd.append("phylogenyTxt", phylogenyTxt);

    setLoading(true);

    try {
  const res = await fetch("/api/bacterial-wgs/generate", {
    method: "POST",
    body: fd
  });
  const data = await res.json();
  setLoading(false);

  if (!res.ok || !data.success) {
    throw new Error(data.error || "Failed to generate report.");
  }

  const downloadFilename = data.filename || `bacterial_wgs_${Date.now()}.html`;
  const fullReportUrl = data.reportUrl?.startsWith("http") ? data.reportUrl : data.reportUrl;
  const pdfFilename = data.pdfFilename || downloadFilename.replace(/\.html$/, ".pdf");
  const fullPdfUrl = (data.pdfUrl || data.reportUrl?.replace(/\.html$/, ".pdf") || "").startsWith("http")
    ? (data.pdfUrl || data.reportUrl?.replace(/\.html$/, ".pdf"))
    : (data.pdfUrl || data.reportUrl?.replace(/\.html$/, ".pdf"));

  const repInfo = {
    htmlUrl: data.reportUrl,
    fullHtmlUrl: fullReportUrl,
    pdfUrl: data.pdfUrl || data.reportUrl?.replace(/\.html$/, ".pdf"),
    fullPdfUrl: fullPdfUrl,
    filename: downloadFilename,
    pdfFilename: pdfFilename,
  };
  setGeneratedReport(repInfo);
  setSuccess(true);

  if (format === "pdf") {
    await triggerHtmlDownload(fullPdfUrl, pdfFilename);
  } else {
    await triggerHtmlDownload(fullReportUrl, downloadFilename);
    window.open(fullReportUrl, "_blank");
  }

  if (setReportUrls) {
    setReportUrls({
      html: data.reportUrl,
      pdf: data.pdfUrl,
      qcExcel: "",
      mappingExcel: "",
      variantExcel: ""
    });
  }

} catch (err) {
  setLoading(false);
  setError(err.message || "Network error while connecting to server.");
}
  };

  return (
    <div className="bac-page">
      {/* Top Breadcrumb Navigation */}
      <div className="bac-breadcrumb">
        <span>Bacterial Whole Genome Sequencing</span>
        <span className="bc-sep">›</span>
        <span>{moduleType === 'bvbrc' ? 'BV-BRC Module' : 'Standard Pipeline'}</span>
        <span className="bc-sep">›</span>
        <span className="bc-active">Upload Files &amp; Generate Report</span>
      </div>

      <div className="bac-content">
        {/* Module Switcher Bar */}
        <div className="module-switcher">
          <button
            type="button"
            className={`module-btn ${moduleType === 'bvbrc' ? 'active' : ''}`}
            onClick={() => handleSwitchModule('bvbrc')}
          >
            <div className="module-btn-content">
              <span style={{ fontSize: 26, lineHeight: 1 }}>🔬</span>
              <div>
                <div className="module-btn-title">BV-BRC Module</div>
                <div className="module-btn-sub">RASTtk Annotation • PATRIC Phylogeny • fastANI • Unicycler v0.4.8</div>
              </div>
            </div>
            {moduleType === 'bvbrc' && <span className="module-badge">Active</span>}
          </button>

          <button
            type="button"
            className={`module-btn ${moduleType === 'standard' ? 'active' : ''}`}
            onClick={() => handleSwitchModule('standard')}
          >
            <div className="module-btn-content">
              <span style={{ fontSize: 26, lineHeight: 1 }}>🧬</span>
              <div>
                <div className="module-btn-title">Standard Pipeline</div>
                <div className="module-btn-sub">Bakta Annotation • Roary Phylogeny • CARD/VFDB Databases</div>
              </div>
            </div>
            {moduleType === 'standard' && <span className="module-badge">Active</span>}
          </button>
        </div>

        {/* Section 1: Client & Project Info */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>1. Client &amp; Project Information (Home Tab)</h3>
              <p>Enter project metadata and contact details for the report cover page</p>
            </div>
          </div>
          <div className="sub-label">General Metadata</div>
          <div className="g2">
            <div className="field">
              <label>Report Title</label>
              <input name="reportTitle" value={ci.reportTitle} onChange={handleCi} placeholder="e.g. Bacterial WGS" />
            </div>
            <div className="field">
              <label>Client Name</label>
              <input name="clientName" value={ci.clientName} onChange={handleCi} placeholder="Dr. Alice Smith" />
            </div>
            <div className="field">
              <label>Project ID <span className="req">*</span></label>
              <input name="projectId" value={ci.projectId} onChange={handleCi} placeholder="e.g. OPL-2026-BAC" />
            </div>
            <div className="field">
              <label>Institution / Company</label>
              <input name="institution" value={ci.institution} onChange={handleCi} placeholder="Harvard Medical School" />
            </div>
            <div className="field">
              <label>Contact Email</label>
              <input name="contact" value={ci.contact} onChange={handleCi} placeholder="alice@harvard.edu" />
            </div>
            <div className="field">
              <label>Salesperson</label>
              <input name="salesperson" value={ci.salesperson} onChange={handleCi} placeholder="John Doe" />
            </div>
          </div>
        </div>

        {/* Section 2: Project Information & Bioinformatics Software */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>2. Project Information &amp; Bioinformatics Software (Project Info Tab)</h3>
              <p>Target organism details, bioinformatics analysis software tools table, and workflow methodology</p>
            </div>
          </div>

          <div className="sub-label">Organism Classification</div>
          <div className="g2">
            <div className="field">
              <label>Species Name <span className="req">*</span></label>
              <input name="speciesName" value={pi.speciesName} onChange={handlePi} placeholder="e.g. Escherichia coli" />
            </div>
            <div className="field">
              <label>Taxonomy String</label>
              <input name="taxonomy" value={pi.taxonomy} onChange={handlePi} placeholder="Bacteria; Pseudomonadota; Gammaproteobacteria; ..." />
            </div>
          </div>

          <div className="divider" />

          <div className="sub-label">Bioinformatics Software &amp; Pipeline Versions</div>
          <table className="software-table">
            <thead>
              <tr>
                <th>Analysis Step / Feature</th>
                <th>Software Tool</th>
                <th>Version</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {softwareRows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input value={r.item} onChange={e => editSoftwareRow(i, "item", e.target.value)} placeholder="e.g. Assembly" />
                  </td>
                  <td>
                    <input value={r.software} onChange={e => editSoftwareRow(i, "software", e.target.value)} placeholder="e.g. SPAdes" />
                  </td>
                  <td>
                    <input value={r.version} onChange={e => editSoftwareRow(i, "version", e.target.value)} placeholder="e.g. v3.15.5" />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button type="button" className="btn-remove-step" onClick={() => removeSoftwareRow(i)} title="Remove row">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="btn-add-step" onClick={addSoftwareRow}>+ Add Analysis Tool</button>

          <div className="divider" />

          <div className="sub-label">Workflow Methodology Description</div>
          <textarea
            className="method-area"
            value={methodologyText}
            onChange={e => setMethodologyText(e.target.value)}
          />
          <div className="method-meta">
            <span className="method-word-count">{wordCount} words | {methodologyText.length} characters</span>
            <button
              type="button"
              className="method-clear"
              onClick={() => setMethodologyText(moduleType === 'bvbrc' ? BVBRC_DEFAULT_METHODOLOGY : DEFAULT_METHODOLOGY)}
            >
              Reset to Default
            </button>
          </div>
        </div>

        {/* Section 3: Data Quality Control (QC) */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>3. Data Quality Control (Data QC Tab)</h3>
              <p>Upload raw &amp; clean sequencing QC metrics, summary files, and quality plot images</p>
            </div>
          </div>

          <div className="sub-label">Raw FastQC / MultiQC Data Statistics</div>
          <div className="g3">
            <div className="field">
              <label>Raw Sequence Counts (.txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={rawSequence}
                mainLabel="Upload Sequence Counts"
                subLabel="fastqc_sequence_counts_plot.txt"
                icon="📊"
                onFile={setRawSequence}
              />
            </div>
            <div className="field">
              <label>Raw MultiQC FastQC Stats (.txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={rawMultiqc}
                mainLabel="Upload MultiQC Stats"
                subLabel="multiqc_general_stats.txt"
                icon="📋"
                onFile={setRawMultiqc}
              />
            </div>
            <div className="field">
              <label>Raw Quality Scores Plot (.tsv / .txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={rawQuality}
                mainLabel="Upload Quality Scores"
                subLabel="fastqc_per_sequence_quality_scores_plot.tsv"
                icon="📈"
                onFile={setRawQuality}
              />
            </div>
          </div>

          <div className="divider" />

          <div className="sub-label">Trimmed / Clean Data Statistics (Optional)</div>
          <div className="g3">
            <div className="field">
              <label>Clean Sequence Counts (.txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={cleanSequence}
                mainLabel="Upload Clean Counts"
                subLabel="fastqc_sequence_counts_plot.txt (clean/trimmed)"
                icon="📊"
                onFile={setCleanSequence}
              />
            </div>
            <div className="field">
              <label>Clean MultiQC FastQC Stats (.txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={cleanMultiqc}
                mainLabel="Upload Clean MultiQC Stats"
                subLabel="multiqc_general_stats.txt (clean/trimmed)"
                icon="📋"
                onFile={setCleanMultiqc}
              />
            </div>
            <div className="field">
              <label>Clean Quality Scores Plot (.tsv / .txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={cleanQuality}
                mainLabel="Upload Clean Quality Scores"
                subLabel="fastqc_per_sequence_quality_scores_plot.tsv (clean)"
                icon="📈"
                onFile={setCleanQuality}
              />
            </div>
          </div>

          <div className="divider" />

          <div className="sub-label">OR Pre-formatted QC Metrics Summary (TSV / CSV — Supports Raw &amp; Clean Data)</div>
          <DropArea
            accept=".tsv,.csv,.txt"
            file={rawQcSummary}
            mainLabel="Drop qc_summary.tsv file here"
            subLabel="TSV with Sample ID, Data Size, Raw Data (Reads, Bases, GC%, Phred, Q30%) and optional Clean Data columns"
            icon="📑"
            onFile={setRawQcSummary}
          />

          <div className="divider" />

          <div className="sub-label">Quality Control Plots (FastQC / MultiQC Images)</div>
          <div className="g2">
            <div className="field">
              <label>1. Per Base Sequence Quality Plot</label>
              <DropArea accept="image/*" file={qcPlot1} mainLabel="Upload Quality Plot 1" subLabel="Per base sequence quality plot" icon="🖼️" onFile={setQcPlot1} />
            </div>
            <div className="field">
              <label>2. Per Sequence Quality Scores Plot</label>
              <DropArea accept="image/*" file={qcPlot2} mainLabel="Upload Quality Plot 2" subLabel="Per sequence quality scores plot" icon="🖼️" onFile={setQcPlot2} />
            </div>
            <div className="field">
              <label>3. Per Sequence GC Content Plot</label>
              <DropArea accept="image/*" file={qcPlot3} mainLabel="Upload GC Plot 3" subLabel="Per sequence GC content plot" icon="🖼️" onFile={setQcPlot3} />
            </div>
            <div className="field">
              <label>4. Sequence Length Distribution Plot</label>
              <DropArea accept="image/*" file={qcPlot4} mainLabel="Upload Length Plot 4" subLabel="Sequence length distribution plot" icon="🖼️" onFile={setQcPlot4} />
            </div>
            <div className="field">
              <label>5. Per Tile Sequence Quality Plot (Optional)</label>
              <DropArea accept="image/*" file={qcPlot5} mainLabel="Upload Tile Plot 5" subLabel="Per tile sequence quality plot" icon="🖼️" onFile={setQcPlot5} />
            </div>
            <div className="field">
              <label>6. Per Base Sequence Content Plot (Optional)</label>
              <DropArea accept="image/*" file={qcPlot6} mainLabel="Upload Content Plot 6" subLabel="Per base sequence content plot" icon="🖼️" onFile={setQcPlot6} />
            </div>
          </div>
        </div>

        {/* Section 4: Genome Assembly */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>4. Genome Assembly (Assembly Tab)</h3>
              <p>Upload QUAST report ZIP archive, Bandage assembly graph image, and Contigs FASTA file</p>
            </div>
          </div>

          <div className="sub-label">De Novo Assembly Quality &amp; Structure</div>
          <div className="g3">
            <div className="field">
              <label>a. QUAST Report Archive (.ZIP)</label>
              <DropArea
                accept=".zip"
                file={quastZip}
                mainLabel="Upload quast.zip"
                subLabel="ZIP containing report.txt / transposed_report.tsv"
                icon="📦"
                onFile={setQuastZip}
              />
            </div>
            <div className="field">
              <label>b. Bandage Assembly Graph Image</label>
              <DropArea
                accept="image/*"
                file={bandagePlot}
                mainLabel="Upload Bandage Graph"
                subLabel="Bandage assembly graph plot image"
                icon="🕸️"
                onFile={setBandagePlot}
              />
            </div>
            <div className="field">
              <label>c. Contigs FASTA File (.fasta / .fa / .fna)</label>
              <DropArea
                accept=".fasta,.fa,.fna,.txt"
                file={contigsFasta}
                mainLabel="Upload contigs.fasta"
                subLabel="De novo assembled contigs sequence file"
                icon="🧬"
                onFile={setContigsFasta}
              />
            </div>
          </div>

          <FileTree
            title="Expected QUAST Archive Structure"
            lines={[
              { type: "root", text: "quast_output.zip" },
              { branch: " ├── ", text: "report.txt", cls: "green" },
              { branch: " ├── ", text: "transposed_report.tsv", cls: "green" },
              { branch: " └── ", text: "basic_stats/", cls: "folder" },
              { branch: "      ├── ", text: "cumulative_plot.pdf", cls: "note" },
              { branch: "      └── ", text: "GC_content_plot.pdf", cls: "note" }
            ]}
          />

          <div className="divider" />

          <div className="sub-label">QUAST Basic Stats Figures / Plots / PDFs</div>
          <DropArea
            accept="image/*,.pdf"
            isMulti={true}
            files={quastPdfs}
            mainLabel="Upload QUAST Basic Stat Images or PDFs"
            subLabel="Select plots from quast_output/basic_stats/ (Cumulative, GC distribution, Nx/N50, etc.)"
            icon="📈"
            onFiles={setQuastPdfs}
          />
          {quastPdfs.length > 0 && (
            <div className="chip-list">
              {quastPdfs.map((f, i) => (
                <div key={i} className="chip">
                  <span>{f.name}</span>
                  <button type="button" onClick={() => setQuastPdfs(quastPdfs.filter((_, idx) => idx !== i))}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 5: Genome Annotation & Target Databases */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>5. Genome Annotation &amp; AMR Databases (Annotation Tab)</h3>
              <p>
                {moduleType === 'bvbrc'
                  ? "Upload BV-BRC Comprehensive Genome Analysis HTML report (FullGenomeReport.html) and Circos genomic map"
                  : "Upload Genome Annotation feature tables, Circos plot image, and CARD/VFDB/TCSB/TTD target database files"}
              </p>
            </div>
          </div>

          {moduleType === 'bvbrc' ? (
            <>
              <div className="sub-label">BV-BRC Comprehensive Genome Report &amp; Circos Map</div>
              <div className="g2">
                <div className="field">
                  <label>a. HTML Upload Section (FullGenomeReport.html) <span className="req">*</span></label>
                  <DropArea
                    accept=".html,.htm"
                    file={fullGenomeReportHtml}
                    mainLabel="Upload FullGenomeReport.html"
                    subLabel="BV-BRC Comprehensive Genome Analysis report HTML (Tables 2, 3, 4, 5)"
                    icon="📄"
                    onFile={setFullGenomeReportHtml}
                  />
                </div>
                <div className="field">
                  <label>b. Circos Image Upload Section</label>
                  <DropArea
                    accept="image/*"
                    file={circosPlot}
                    mainLabel="Upload Circos Plot Image"
                    subLabel="Circular genome visualization atlas (.png, .jpg, .svg)"
                    icon="⭕"
                    onFile={setCircosPlot}
                  />
                </div>
              </div>

              <div style={{ marginTop: 18 }}>
                <button
                  type="button"
                  onClick={() => setShowAdvancedAnn(p => !p)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    color: dark ? "#14b8a6" : "#0d9488",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 0"
                  }}
                >
                  <span>{showAdvancedAnn ? "▼" : "▶"}</span>
                  <span>{showAdvancedAnn ? "Hide Individual Database & Feature Uploads (Optional Fallback)" : "Show Individual Database & Feature Uploads (Optional Fallback)"}</span>
                </button>
              </div>

              {showAdvancedAnn && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px dashed ${dark ? '#1e3a5f' : '#e2e8f0'}` }}>
                  <div className="sub-label">Individual Database &amp; Feature Files</div>
                  <div className="g2" style={{ marginBottom: 14 }}>
                    <div className="field">
                      <label>Annotated Genome Features (Table 1 TSV / CSV)</label>
                      <DropArea accept=".tsv,.csv,.txt" file={annotatedGenomeFeatures} mainLabel="Upload Annotated Genome Features" subLabel="CSV/TSV with Genomic Feature & Count (CDS, tRNA, rRNA...)" icon="📊" onFile={setAnnotatedGenomeFeatures} />
                    </div>
                    <div className="field">
                      <label>Protein Features (Table 2 TSV / CSV)</label>
                      <DropArea accept=".tsv,.csv,.txt" file={proteinFeatures} mainLabel="Upload Protein Features" subLabel="CSV/TSV with Protein Feature & Count (Hypothetical, Functional...)" icon="🧬" onFile={setProteinFeatures} />
                    </div>
                  </div>
                  <div className="g2">
                    <div className="field">
                      <label>CARD AMR Database Text File</label>
                      <DropArea accept=".txt,.tsv" file={cardTxt} mainLabel="Upload card.txt" subLabel="CARD resistance genes text file" icon="🛡️" onFile={setCardTxt} />
                    </div>
                    <div className="field">
                      <label>VFDB Virulence Factor Text File</label>
                      <DropArea accept=".txt,.tsv" file={vfdbTxt} mainLabel="Upload vfdb.txt" subLabel="VFDB virulence factors file" icon="📄" onFile={setVfdbTxt} />
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="sub-label">Genome Annotation Feature Tables (Table 1 &amp; Table 2)</div>
              <div className="g2">
                <div className="field">
                  <label>Annotated Genome Features (Table 1)</label>
                  <DropArea accept=".tsv,.csv,.txt" file={annotatedGenomeFeatures} mainLabel="Upload Annotated Genome Features" subLabel="CSV/TSV with Genomic Feature & Count (CDS, tRNA, rRNA...)" icon="📊" onFile={setAnnotatedGenomeFeatures} />
                </div>
                <div className="field">
                  <label>Protein Features (Table 2)</label>
                  <DropArea accept=".tsv,.csv,.txt" file={proteinFeatures} mainLabel="Upload Protein Features" subLabel="CSV/TSV with Protein Feature & Count (Hypothetical, Functional...)" icon="🧬" onFile={setProteinFeatures} />
                </div>
              </div>

              <div className="divider" />

              <div className="sub-label">Full Annotation &amp; Circos Plot</div>
              <div className="g2">
                <div className="field">
                  <label>Annotation File (Bakta TSV / CSV)</label>
                  <DropArea accept=".tsv,.csv,.txt" file={annotationTsv} mainLabel="Upload annotation.tsv / features.txt" subLabel="Features file with types & protein annotations" icon="📑" onFile={setAnnotationTsv} />
                </div>
                <div className="field">
                  <label>Genomic Circos Plot Image</label>
                  <DropArea accept="image/*" file={circosPlot} mainLabel="Upload Circos Plot Image" subLabel="Circular genome visualization plot" icon="⭕" onFile={setCircosPlot} />
                </div>
              </div>

              <div className="divider" />

              <div className="sub-label">Target Database Text Files (CARD, TCSB, VFDB, TTD)</div>
              <div className="g2">
                <div className="field">
                  <label>CARD AMR Database Text File</label>
                  <DropArea accept=".txt,.tsv" file={cardTxt} mainLabel="Upload card.txt" subLabel="CARD resistance genes text file" icon="🛡️" onFile={setCardTxt} />
                </div>
                <div className="field">
                  <label>TCSB Database Text File</label>
                  <DropArea accept=".txt,.tsv" file={tcsbTxt} mainLabel="Upload tcsb.txt" subLabel="TCSB target genes text file" icon="📄" onFile={setTcsbTxt} />
                </div>
                <div className="field">
                  <label>VFDB Virulence Factor Text File</label>
                  <DropArea accept=".txt,.tsv" file={vfdbTxt} mainLabel="Upload vfdb.txt" subLabel="VFDB virulence factors file" icon="📄" onFile={setVfdbTxt} />
                </div>
                <div className="field">
                  <label>TTD Database Text File</label>
                  <DropArea accept=".txt,.tsv" file={ttdTxt} mainLabel="Upload ttd.txt" subLabel="TTD drug targets file" icon="📄" onFile={setTtdTxt} />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Section 6: Phylogeny Analysis */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>6. Phylogeny Analysis (Phylogeny Tab)</h3>
              <p>
                {moduleType === 'bvbrc'
                  ? "Upload phylogenetic tree plot image constructed via PATRIC and optional fastANI matrix"
                  : "Upload phylogenetic tree plot image (Roary) and Average Nucleotide Identity (fastANI) matrix or phylogeny dataset"}
              </p>
            </div>
          </div>
          <div className="g2">
            <div className="field">
              <label>Phylogenetic Tree Image (PATRIC) <span className="req">*</span></label>
              <DropArea
                accept="image/*"
                file={phylogenyImage}
                mainLabel="Upload Phylogenetic Tree Image"
                subLabel="Phylogenetic tree visualization figure (.png, .jpg, .svg)"
                icon="🌳"
                onFile={setPhylogenyImage}
              />
            </div>
            <div className="field">
              <label>Average Nucleotide Identity (fastANI) / Phylogeny File (Optional)</label>
              <DropArea
                accept=".txt,.tsv,.csv,.nwk,.tree"
                file={phylogenyTxt}
                mainLabel="Upload ani.txt / Phylogeny Dataset"
                subLabel="Average Nucleotide Identity (fastANI) matrix or tree file"
                icon="📊"
                onFile={setPhylogenyTxt}
              />
            </div>
          </div>
        </div>

                {/* Section 7: Data Files & Deliverables */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>7. Data Files &amp; Deliverables (Resources Tab)</h3>
              <p>
                Upload a single ZIP containing only the important files you want listed
                in the Resources → Data Files &amp; Deliverables section of the report.
              </p>
            </div>
          </div>

          <div className="info-banner">
            <div className="info-banner-icon">💡</div>
            <div className="info-banner-text">
              <strong>Tip:</strong> Put only the final deliverables inside this ZIP
              (e.g. annotation TSV, CARD/VFDB results, Circos, Bandage, final plots,
              ANI matrix, etc.). Intermediate FastQC plots and raw MultiQC files will
              no longer appear automatically.
            </div>
          </div>

          <DropArea
            accept=".zip"
            file={resourcesZip}
            mainLabel="Upload Resources / Deliverables ZIP"
            subLabel="One ZIP file – contents will be extracted and listed in the report"
            icon="📦"
            onFile={setResourcesZip}
          />
        </div>
      </div>

      {/* Fixed Bottom Submit Action Bar */}
      <div className="submit-bar">
        {error && <div className="submit-error">⚠️ {error}</div>}
        {success && generatedReport && (
          <div className="submit-success" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span>✓ Report Generated!</span>
            <a
              href={generatedReport.fullHtmlUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#15803d", fontWeight: 700, textDecoration: "underline" }}
            >
              Open HTML ↗
            </a>
            <button
              type="button"
              onClick={() => handleUpload('pdf')}
              style={{
                background: "none",
                border: "none",
                color: "#15803d",
                fontWeight: 700,
                textDecoration: "underline",
                cursor: "pointer",
                padding: 0
              }}
            >
              Download PDF 📄
            </button>
          </div>
        )}
        <button
          type="button"
          className="btn-submit"
          onClick={() => handleUpload('html')}
          disabled={loading}
        >
          {loading && actionType === 'html' ? (
            <>
              <span style={{ width: 14, height: 14, borderWidth: 2, borderStyle: "solid", borderColor: "rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "bac-spin .8s linear infinite" }} />
              Generating Report…
            </>
          ) : (
            <> Upload &amp; Generate Report</>
          )}
        </button>

        <button
          type="button"
          className="btn-submit btn-submit-pdf"
          onClick={() => handleUpload('pdf')}
          disabled={loading}
          style={{ background: "#e11d48", boxShadow: "0 4px 14px rgba(225,29,72,0.3)" }}
        >
          {loading && actionType === 'pdf' ? (
            <>
              <span style={{ width: 14, height: 14, borderWidth: 2, borderStyle: "solid", borderColor: "rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "bac-spin .8s linear infinite" }} />
              Exporting PDF…
            </>
          ) : (
            <>📄 Export PDF Report</>
          )}
        </button>
      </div>
    </div>
  );
}